import React from 'react';
import PropTypes from 'prop-types';

import { ROUTE_TYPE } from '../../routes/constants';
import { NavLink } from 'react-router-dom';
import { List, ListItem, Divider } from '@material-ui/core';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons';
import MenuItem from './MenuItem';
import { ROLE_MASTER_ADMIN } from '../settings/ManageUsers/constants';
import { isRouteHiddenForUser } from '../../constants/roleMenuVisibility';

class MenuItems extends React.PureComponent {
  constructor(props) {
    super(props);
    const userAgent = navigator.userAgent;
    const isiOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    this.state = {
      collapse: false,
      isMobile: window.matchMedia('(max-width: 800px)').matches || isiOSDevice,
    };
  }

  componentDidMount = () => {
    if (this.props.collapsable) {
      this.setState({
        collapse: this.props.collapsable,
      });
    }

    this.mediaQuery = window.matchMedia('(max-width: 800px)');
    this.mediaQuery.addEventListener('change', this.handleScreenChange);
  };

  componentWillUnmount = () => {
    this.mediaQuery.removeEventListener('change', this.handleScreenChange);
  };

  hasRouteAccess = (item) => {
    const userRoutes = this.props.userRoutes || [];
    // Screens that would be empty for this user never appear, even though
    // the route is technically granted — either because the role can never
    // have data there, or because view-only strips the screen bare.
    // See constants/roleMenuVisibility.js.
    if (
      isRouteHiddenForUser(
        item.id,
        this.props.user?.authority,
        this.props.user?.accessMode === 'VIEW'
      )
    ) {
      return false;
    }
    if (userRoutes.includes(item.id)) return true;
    if (
      item.requiresAnyOf &&
      item.requiresAnyOf.some((id) => userRoutes.includes(id))
    ) {
      return true;
    }
    return false;
  };

  getMenuItem = (item, index) => {
    const { isMobile } = this.state;

    const isCustomerMobile = this.props.userRoutes?.includes('customer-mobile');
    const isRaffleMobile = this.props.userRoutes?.includes('raffle-mobile');

    const isMasterAdmin =
      this.props.user?.authority?.includes(ROLE_MASTER_ADMIN);

    const hasAccess = this.hasRouteAccess(item);

    if (isCustomerMobile) {
      // when "customer-mobile" exists
      const allowedRoutes = [
        'dashboard',
        'customers',
        'transaction',
        'ticket-out',
        'bonus',
        'add-machine-reading',
        'machine-picture',
        'report',
        'master-reading',
        'point-watching',
        'employee-session',
        'note',
        'settings',
        'manage-rule',
        'manage-users',
        'assign-machine',
        'machine',
        'game',
        'setup-machines',
        'manage-bonus',
        'company',
        'accounting',
        'customer-loan-report',
        'employee-short-report',
        'zelle-report',
        'reading',
        'operation-monitor',
        'atm-transaction',
        'maintenance-permit',
        'boost',
      ];
      if (isRaffleMobile) {
        allowedRoutes.push('raffle');
      }

      if (isMobile && (!allowedRoutes.includes(item.id) || !hasAccess)) {
        return null;
      }
    } else {
      const allowedRoutes = [
        'dashboard',
        'transaction',
        'ticket-out',
        'bonus',
        'add-machine-reading',
        'machine-picture',
        'report',
        'master-reading',
        'point-watching',
        'employee-session',
        'note',
        'settings',
        'manage-rule',
        'manage-users',
        'assign-machine',
        'machine',
        'game',
        'setup-machines',
        'manage-bonus',
        'company',
        'accounting',
        'customer-loan-report',
        'employee-short-report',
        'zelle-report',
        'reading',
        'operation-monitor',
        'atm-transaction',
        'maintenance-permit',
        'boost',
      ];

      if (isMasterAdmin) {
        allowedRoutes.push('customers');
      }

      if (isRaffleMobile) {
        allowedRoutes.push('raffle');
      }

      // when "customer-mobile" does NOT exist
      if (isMobile && (!allowedRoutes.includes(item.id) || !hasAccess)) {
        return null;
      }
    }
    switch (item.type) {
      case ROUTE_TYPE.DIVIDER:
        return (
          <Divider
            key={index}
            style={{ backgroundColor: '#282c5e', height: '2.5px' }}
          />
        );
      case ROUTE_TYPE.MENU_ITEM: {
        const { isDrawer, closeDrawer, depth, depthStep } = this.props;
        if (hasAccess) {
          return (
            <NavLink
              key={item.id}
              to={item.path}
              style={{ textDecoration: 'none' }}
            >
              <ListItem
                key={`${item.id}-${index}`}
                className="leftMenu-item left-menu-navigation"
                dense
                onClick={() => isDrawer && closeDrawer()}
              >
                <MenuItem
                  className="leftMenu-item"
                  item={item}
                  depthPaddingLeft={depth * depthStep}
                />
              </ListItem>
            </NavLink>
          );
        }
        return null;
      }
      case ROUTE_TYPE.SUB_MENU:
        if (hasAccess) {
          return (
            <MenuItems
              key={`${item.id}-${index}`}
              routes={item.submenuItems}
              collapsable={true}
              depth={this.props.depth + 1}
              label={item.label}
              icon={item.icon}
              // Carried into sub-menus so the role checks in getMenuItem
              // (hidden-route filter, isMasterAdmin) behave the same at
              // any depth. Without this they silently no-op one level down.
              user={this.props.user}
              userRoutes={this.props.userRoutes}
              isDrawer={this.props.isDrawer}
              closeDrawer={this.props.closeDrawer}
            />
          );
        }
        return null;
      default:
        return <ListItem />;
    }
  };

  toggleCollapse = () => {
    this.setState((prevState) => ({
      collapse: !prevState.collapse,
    }));
  };

  getCollapableItem = () => {
    return (
      <ListItem
        key={this.props.id}
        className="leftMenu-item"
        button
        dense
        onClick={this.toggleCollapse}
      >
        <div className="leftMenu-item-content">
          <div className="leftMenu-item-text">{this.props.label}</div>
        </div>
        {this.state.collapse ? <ExpandMoreIcon /> : <ExpandLessIcon />}
      </ListItem>
    );
  };

  render() {
    return (
      <List disablePadding>
        {this.props.collapsable && this.getCollapableItem()}
        {!this.state.collapse &&
          this.props.routes.map((route, index) => {
            if (route.sidebar) {
              return this.getMenuItem(route, index);
            } else {
              return null;
            }
          })}

        <Divider style={{ backgroundColor: '#282c5e', height: '0.2px' }} />
      </List>
    );
  }
}

MenuItems.defaultProps = {
  routes: [],
  collapsable: false,
  depth: 0,
  depthStep: 20,
};

MenuItems.propTypes = {
  routes: PropTypes.array.isRequired,
  collapsable: PropTypes.bool,
  depth: PropTypes.number,
  depthStep: PropTypes.number,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  icon: PropTypes.object,
  userRoutes: PropTypes.array,
  isDrawer: PropTypes.bool,
  closeDrawer: PropTypes.func,
};

export default MenuItems;
